import { useEffect, useRef } from 'react';

import './AutoMarquee.css';

const DEFAULT_SPEED = 40; // px per second
const DEFAULT_MIN_DURATION = 8; // seconds
const DEFAULT_MAX_DURATION = 40; // seconds

function parseCssNumber(value, fallback) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export default function AutoMarquee({
  children,
  className = '',
  contentClassName = '',
  dependencies = [],
}) {
  const rootRef = useRef(null);
  const itemRef = useRef(null);
  const effectDependencies = Array.isArray(dependencies) ? dependencies : [dependencies];

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const root = rootRef.current;
    const item = itemRef.current;

    if (!root || !item) {
      return undefined;
    }

    let frameId = null;

    const updateMarquee = () => {
      if (!root || !item) {
        return;
      }

      const rootWidth = root.clientWidth;
      const contentWidth = item.scrollWidth;

      const hasOverflow = contentWidth - rootWidth > 1;

      if (!hasOverflow) {
        root.dataset.marqueeActive = 'false';
        root.style.removeProperty('--auto-marquee-distance');
        root.style.removeProperty('--auto-marquee-duration');
        root.style.removeProperty('--auto-marquee-gap');
        return;
      }

      const computedStyle = window.getComputedStyle(root);

      const gap = parseCssNumber(computedStyle.getPropertyValue('--auto-marquee-gap'), 32);
      const speed = parseCssNumber(computedStyle.getPropertyValue('--auto-marquee-speed'), DEFAULT_SPEED);
      const minDuration = parseCssNumber(
        computedStyle.getPropertyValue('--auto-marquee-min-duration'),
        DEFAULT_MIN_DURATION,
      );
      const maxDuration = parseCssNumber(
        computedStyle.getPropertyValue('--auto-marquee-max-duration'),
        DEFAULT_MAX_DURATION,
      );

      const distance = contentWidth + gap;
      const rawDuration = distance / Math.max(speed, 1);
      const duration = Math.min(Math.max(rawDuration, minDuration), maxDuration);

      root.style.setProperty('--auto-marquee-gap', `${gap}px`);
      root.style.setProperty('--auto-marquee-distance', `${distance}px`);
      root.style.setProperty('--auto-marquee-duration', `${duration}s`);
      root.dataset.marqueeActive = 'true';
    };

    const scheduleUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(updateMarquee);
    };

    scheduleUpdate();

    const resizeObserverSupported = typeof ResizeObserver !== 'undefined';
    let resizeObserver;

    if (resizeObserverSupported) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(root);
      resizeObserver.observe(item);
    } else {
      window.addEventListener('resize', scheduleUpdate);
    }

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', scheduleUpdate);
      }
    };
  }, effectDependencies);

  const rootClassName = ['auto-marquee', className].filter(Boolean).join(' ');
  const contentClass = ['auto-marquee__item', contentClassName].filter(Boolean).join(' ');

  return (
    <div className={rootClassName} ref={rootRef} data-marquee-active="false">
      <div className="auto-marquee__track" aria-live="polite">
        <div className={contentClass} ref={itemRef}>
          {children}
        </div>
        <div className={`${contentClass} auto-marquee__item--clone`} aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
